import { z } from "zod";
import { RoleSchema } from "./roles.js";

/**
 * Department & subdivision forms — Google-Forms-style data collection
 * owned by a department or one of its subdivisions.
 *
 * A form is composed of:
 *   - a list of typed fields (text, choice, file_upload, …) the
 *     respondent fills out, and
 *   - two audience rule sets — one controlling who can SEE & SUBMIT
 *     the form, another controlling who can VIEW the responses.
 *
 * Authoring (create / edit / delete) is gated by the department-level
 * `manage_department` permission on the owning department, and the
 * form creator + any `manage_department` holder always passes the
 * audience check implicitly.
 *
 * File uploads accept only a strict allowlist of extensions/MIMEs —
 * see `apps/api/src/services/form-uploads.ts` for the runtime check.
 */

// ────────────────────────────────────────────────────────────────────────────
// Field types
// ────────────────────────────────────────────────────────────────────────────

/**
 * Safe upload extensions — kept in sync with
 * `apps/api/src/services/form-uploads.ts::SAFE_UPLOAD_EXTENSIONS`.
 * Documents, audio, and video only — no executables, archives, HTML,
 * or office formats with macros.
 */
export const SAFE_UPLOAD_EXTENSIONS = [
  // Documents
  ".pdf",
  ".txt",
  // Audio
  ".mp3",
  ".wav",
  ".ogg",
  ".m4a",
  ".flac",
  // Video
  ".mp4",
  ".webm",
  ".mov",
] as const;

export const SafeUploadExtensionSchema = z.enum(SAFE_UPLOAD_EXTENSIONS);
export type SafeUploadExtension = z.infer<typeof SafeUploadExtensionSchema>;

const FormFieldBase = z.object({
  id: z.string().uuid(),
  label: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  required: z.boolean().default(false),
});

const ShortTextField = FormFieldBase.extend({
  type: z.literal("short_text"),
  maxLength: z.number().int().positive().max(500).default(200),
});
const LongTextField = FormFieldBase.extend({
  type: z.literal("long_text"),
  maxLength: z.number().int().positive().max(10_000).default(2000),
});
const NumberField = FormFieldBase.extend({
  type: z.literal("number"),
  min: z.number().optional(),
  max: z.number().optional(),
});
const EmailField = FormFieldBase.extend({ type: z.literal("email") });
const DateField = FormFieldBase.extend({ type: z.literal("date") });
const SingleChoiceField = FormFieldBase.extend({
  type: z.literal("single_choice"),
  options: z.array(z.string().min(1).max(200)).min(1).max(50),
});
const MultiChoiceField = FormFieldBase.extend({
  type: z.literal("multi_choice"),
  options: z.array(z.string().min(1).max(200)).min(1).max(50),
  minSelections: z.number().int().nonnegative().optional(),
  maxSelections: z.number().int().positive().optional(),
});
const DropdownField = FormFieldBase.extend({
  type: z.literal("dropdown"),
  options: z.array(z.string().min(1).max(200)).min(1).max(100),
});
const FileUploadField = FormFieldBase.extend({
  type: z.literal("file_upload"),
  maxFiles: z.number().int().positive().max(10).default(1),
  /** Subset of {@link SAFE_UPLOAD_EXTENSIONS} the creator wants to accept. */
  allowedExtensions: z.array(SafeUploadExtensionSchema).min(1),
});
const SectionHeader = FormFieldBase.extend({ type: z.literal("section_header") });

export const FormFieldSchema = z.discriminatedUnion("type", [
  ShortTextField,
  LongTextField,
  NumberField,
  EmailField,
  DateField,
  SingleChoiceField,
  MultiChoiceField,
  DropdownField,
  FileUploadField,
  SectionHeader,
]);
export type FormField = z.infer<typeof FormFieldSchema>;

export const FormFieldsSchema = z.array(FormFieldSchema).max(100);
export type FormFields = z.infer<typeof FormFieldsSchema>;

export const FORM_FIELD_TYPES = [
  "short_text",
  "long_text",
  "number",
  "email",
  "date",
  "single_choice",
  "multi_choice",
  "dropdown",
  "file_upload",
  "section_header",
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

// ────────────────────────────────────────────────────────────────────────────
// Audience rules — "match any"
// ────────────────────────────────────────────────────────────────────────────

const AudienceRuleHubRole = z.object({
  type: z.literal("hub_role"),
  minRole: RoleSchema,
});
const AudienceRuleRankFloor = z.object({
  type: z.literal("rank_floor"),
  departmentId: z.number().int().positive(),
  /** Rank id; runtime check loads its `priority` and compares against viewer's rank in dept. */
  minRankId: z.number().int().positive(),
});

export const AudienceRuleSchema = z.discriminatedUnion("type", [
  AudienceRuleHubRole,
  AudienceRuleRankFloor,
]);
export type AudienceRule = z.infer<typeof AudienceRuleSchema>;

/** An empty audience matches **nobody** (excluding the implicit creator/manager override). */
export const AudienceSchema = z.array(AudienceRuleSchema).max(20);
export type Audience = z.infer<typeof AudienceSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Form + response
// ────────────────────────────────────────────────────────────────────────────

export const DepartmentFormStatusSchema = z.enum(["draft", "open", "closed"]);
export const DepartmentFormKindSchema = z.enum(["form", "application"]);
export const DepartmentApplicationAudienceSchema = z.enum(["department_members", "all_signed_in"]);

export type DepartmentFormStatus = z.infer<typeof DepartmentFormStatusSchema>;

export const DepartmentFormSchema = z.object({
  id: z.number(),
  departmentId: z.number(),
  /** When set, the form is scoped to the subdivision instead of the whole department. */
  subdivisionId: z.number().nullable(),
  kind: DepartmentFormKindSchema.default("form"),
  applicationAudience: DepartmentApplicationAudienceSchema.default("department_members"),
  title: z.string(),
  description: z.string().nullable(),
  status: DepartmentFormStatusSchema,
  fields: FormFieldsSchema,
  respondentAudience: AudienceSchema,
  viewerAudience: AudienceSchema,
  allowMultipleSubmissions: z.boolean(),
  allowEditAfterSubmit: z.boolean(),
  createdById: z.number().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DepartmentForm = z.infer<typeof DepartmentFormSchema>;

/**
 * Per-row flags computed server-side so the web doesn't have to replicate
 * the audience evaluator. Always present on list / detail responses.
 */
export const FormViewerCapsSchema = z.object({
  canSubmit: z.boolean(),
  canViewResponses: z.boolean(),
  canManage: z.boolean(),
  canReviewApplications: z.boolean(),
  /** ID of the caller's existing response on this form, if any. */
  ownResponseId: z.number().nullable(),
});
export type FormViewerCaps = z.infer<typeof FormViewerCapsSchema>;

export const DepartmentFormSummarySchema = DepartmentFormSchema.extend({
  responseCount: z.number().int().nonnegative(),
  caps: FormViewerCapsSchema,
});
export type DepartmentFormSummary = z.infer<typeof DepartmentFormSummarySchema>;

export const DepartmentFormDetailSchema = DepartmentFormSchema.extend({
  caps: FormViewerCapsSchema,
});
export type DepartmentFormDetail = z.infer<typeof DepartmentFormDetailSchema>;

export const CreateDepartmentFormBodySchema = DepartmentFormSchema.pick({
  subdivisionId: true,
  kind: true,
  applicationAudience: true,
  title: true,
  description: true,
  fields: true,
  respondentAudience: true,
  viewerAudience: true,
  allowMultipleSubmissions: true,
  allowEditAfterSubmit: true,
}).extend({
  description: z.string().max(2000).nullable().optional(),
  status: DepartmentFormStatusSchema.optional(),
});
export type CreateDepartmentFormBody = z.infer<typeof CreateDepartmentFormBodySchema>;

export const PatchDepartmentFormBodySchema = CreateDepartmentFormBodySchema.partial().extend({
  status: DepartmentFormStatusSchema.optional(),
});
export type PatchDepartmentFormBody = z.infer<typeof PatchDepartmentFormBodySchema>;

// ── Response answers ────────────────────────────────────────────────────────

export const ResponseAnswerSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), value: z.string() }),
  z.object({ type: z.literal("number"), value: z.number() }),
  z.object({ type: z.literal("choice"), value: z.string() }),
  z.object({ type: z.literal("choices"), value: z.array(z.string()) }),
  z.object({ type: z.literal("date"), value: z.string() }), // ISO date string
  z.object({ type: z.literal("files"), value: z.array(z.string().uuid()) }),
]);
export type ResponseAnswer = z.infer<typeof ResponseAnswerSchema>;

/** Map of field UUID → answer. */
export const ResponseAnswersSchema = z.record(z.string().uuid(), ResponseAnswerSchema);
export type ResponseAnswers = z.infer<typeof ResponseAnswersSchema>;

export const FormResponseFileSchema = z.object({
  id: z.string().uuid(),
  fieldId: z.string().uuid(),
  originalName: z.string(),
  mimetype: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export type FormResponseFile = z.infer<typeof FormResponseFileSchema>;

export const FormResponseSchema = z.object({
  id: z.number(),
  status: z.enum(["pending", "approved", "denied"]).default("pending"),
  reviewNote: z.string().nullable().default(null),
  reviewerId: z.number().nullable().default(null),
  reviewerName: z.string().nullable().default(null),
  decidedAt: z.string().nullable().default(null),
  formId: z.number(),
  respondentId: z.number(),
  respondentName: z.string().nullable(),
  answers: ResponseAnswersSchema,
  files: z.array(FormResponseFileSchema),
  submittedAt: z.string(),
  updatedAt: z.string(),
});
export type FormResponse = z.infer<typeof FormResponseSchema>;
